'use client';

import { Settings } from 'lucide-react';
import React from 'react';

import { cn } from '@ui/lib/utils';
import { useCurrentProfessional } from '@website/hooks/useCurrentProfessional';

interface ProfileNavIconProps {
  className?: string;
}

/**
 * The gear icon used in the header / mobile bottom nav to open the
 * settings/profile hub. The tooltip label "Settings" / "Paramètres" frames
 * this as an entry point to all account-level preferences (profile,
 * notifications, payments, language, mode switch, legal, etc.), which is why
 * we use a generic settings gear rather than a person silhouette or brand
 * mark — the destination is broader than "just your profile".
 *
 * A small attention dot is overlaid whenever the authenticated mentor has
 * outstanding onboarding actions — i.e. `professional.status === 'incomplete'`.
 * We deliberately do NOT dot on `pending` (ball is in the admin's court, there
 * is nothing the mentor can do) or on `approved`. Legacy `rejected` rows are
 * normalized to `incomplete` to match the backend migration and
 * `reevaluateProfessionalStatus`.
 *
 * The dot is purely visual — clicking the icon still routes to the same
 * settings hub, where the "Complete your profile" menu entry surfaces the
 * actual action.
 */
export const ProfileNavIcon: React.FC<ProfileNavIconProps> = ({ className }) => {
  const { professional, isProfessional } = useCurrentProfessional();

  const normalizedStatus =
    professional?.status === 'approved' ? 'approved' : professional?.status === 'pending' ? 'pending' : 'incomplete';

  const needsAttention = isProfessional && !!professional && normalizedStatus === 'incomplete';

  return (
    <span className="relative inline-flex">
      <Settings className={className} strokeWidth={1.5} />
      {needsAttention && (
        <span
          className={cn(
            'absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-amber-500 ring-2 ring-white',
            'animate-pulse'
          )}
          aria-label="Action required"
        />
      )}
    </span>
  );
};
